import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { awsConfig } from "../aws-config";
import { getAuthHeader } from "../api";
import { formatDate } from "../utils/dateFormat";

interface Payment {
  id: string;
  amount: number;
  description: string;
  date: string;
  status: string;
  category: string;
  jobSize: string;
}

interface Summary {
  totalLeads: number;
  freeLeadsUsed: number;
  paidLeads: number;
  totalSpent: number;
  freeLeadsRemaining: number;
}

interface Earnings {
  totalEarnings: number;
  thisMonthEarnings: number;
  lastMonthEarnings: number;
  thisWeekEarnings: number;
  avgJobValue: number;
  growthPercent: number;
  completedJobs: number;
  activeJobs: number;
  totalJobs: number;
  monthlyData: Array<{month: string; jobs: number; earnings: number}>;
  netProfit: number;
}

const CATEGORY_ICONS: Record<string, string> = {
  Electrical: "⚡", Cleaning: "🧹", Plumbing: "🔧",
  Outdoor: "🌿", Painting: "🎨", Moving: "🚚", General: "🏠",
};

export default function ProEarningsPage() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [earnings, setEarnings] = useState<Earnings | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    async function load() {
      try {
        const headers = await getAuthHeader();
        const response = await axios.get(`${awsConfig.apiUrl}/pro/earnings`, { headers });
        setPayments(response.data.payments || []);
        setSummary(response.data.summary);
        setEarnings(response.data.earnings);
      } catch (err) { console.error("Failed to load earnings:", err); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  const totalThisMonth = payments
    .filter(p => new Date(p.date).getMonth() === new Date().getMonth())
    .reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-5 pb-0 sticky top-0 z-20 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <button onClick={() => navigate("/home")} className="text-gray-400 text-sm font-semibold mb-1 flex items-center gap-1">
              ← Dashboard
            </button>
            <h1 className="font-display text-2xl font-bold text-navy">Earnings & Billing</h1>
          </div>
          <button onClick={() => navigate("/lead-pricing")} className="bg-blue-pale text-mouna-blue text-xs font-bold rounded-full px-3 py-2">
            Pricing →
          </button>
        </div>
        <div className="flex gap-1">
          {[["overview", "Overview"], ["history", "Payment History"]].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={"flex-1 py-3 text-xs font-bold transition-all relative " + (activeTab === key ? "text-mouna-blue" : "text-gray-400")}>
              {label}
              {activeTab === key && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-mouna-blue rounded-full"/>}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">
        {loading && (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-2xl p-4">
                <div className="skeleton h-4 w-1/3 rounded mb-2"></div>
                <div className="skeleton h-8 w-1/2 rounded"></div>
              </div>
            ))}
          </div>
        )}

        {!loading && summary && activeTab === "overview" && (
          <div className="animate-fade-in">
            {earnings && (
              <>
                {/* Earnings hero card */}
                <div className="bg-gradient-to-br from-teal to-green-500 rounded-2xl p-5 mb-4 text-white">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-xs text-teal-100 uppercase tracking-wider mb-1">Total Earnings</div>
                      <div className="font-mono text-4xl font-bold">${earnings.totalEarnings.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                    </div>
                    {earnings.growthPercent !== 0 && (
                      <div className={"text-xs font-bold rounded-full px-2.5 py-1 " + (earnings.growthPercent > 0 ? "bg-white bg-opacity-20" : "bg-red-500 bg-opacity-30")}>
                        {earnings.growthPercent > 0 ? "↑" : "↓"} {Math.abs(earnings.growthPercent)}%
                      </div>
                    )}
                  </div>
                  <div className="flex gap-4 text-xs text-teal-100">
                    <span>✅ {earnings.completedJobs} completed</span>
                    <span>🔄 {earnings.activeJobs} active</span>
                    <span>💰 ${earnings.avgJobValue.toFixed(0)} avg</span>
                  </div>
                </div>

                {/* Period stats */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { label: "This Week", value: earnings.thisWeekEarnings, icon: "📅" },
                    { label: "This Month", value: earnings.thisMonthEarnings, icon: "📊" },
                    { label: "Last Month", value: earnings.lastMonthEarnings, icon: "🗓️" },
                  ].map(stat => (
                    <div key={stat.label} className="bg-white rounded-2xl p-3 shadow-card text-center">
                      <div className="text-xl mb-1">{stat.icon}</div>
                      <div className="font-bold text-navy text-sm">${stat.value.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
                    </div>
                  ))}
                </div>

                {/* Monthly chart */}
                <div className="bg-white rounded-2xl p-4 shadow-card mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-bold text-navy text-sm">Last 6 Months</div>
                    <div className="text-xs text-gray-400">Earnings</div>
                  </div>
                  <div className="flex items-end justify-between gap-2 h-32">
                    {earnings.monthlyData.map((m, i) => {
                      const maxVal = Math.max(...earnings.monthlyData.map(d => d.earnings), 100);
                      const h = Math.max((m.earnings / maxVal) * 100, 8);
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <div className="text-xs font-bold text-navy">${m.earnings.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                          <div className="w-full bg-blue-pale rounded-lg relative" style={{height: "80px"}}>
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-mouna-blue to-teal rounded-lg transition-all"
                                style={{height: h + "%"}}/>
                          </div>
                          <div className="text-xs text-gray-400">{m.month}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Net profit */}
                <div className="bg-white rounded-2xl p-4 shadow-card mb-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Net Profit</div>
                    <div className="font-bold text-navy text-xl">${earnings.netProfit.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                    <div className="text-xs text-gray-400 mt-0.5">Earnings minus lead costs</div>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-teal-light flex items-center justify-center text-2xl">💰</div>
                </div>
              </>
            )}

            <div className="bg-gradient-to-br from-navy via-mouna-blue to-blue-600 rounded-2xl p-5 mb-4 text-white">
              <div className="text-xs text-blue-200 uppercase tracking-wider mb-1">Total Lead Spend</div>
              <div className="font-mono text-4xl font-bold mb-3">${summary.totalSpent.toFixed(2)}</div>
              <div className="flex gap-4 text-xs text-blue-200">
                <span>📊 {summary.totalLeads} total leads</span>
                <span>💳 {summary.paidLeads} paid</span>
              </div>
            </div>

            {summary.freeLeadsRemaining > 0 && (
              <div className="bg-gradient-to-r from-teal to-green-500 rounded-2xl p-4 text-white mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center text-xl flex-shrink-0">🎁</div>
                <div>
                  <div className="font-bold text-sm">Free Trial Active</div>
                  <div className="text-xs opacity-90">{summary.freeLeadsRemaining} free lead{summary.freeLeadsRemaining !== 1 ? "s" : ""} remaining</div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-5">
              {[
                { label: "This Month", value: "$" + totalThisMonth.toFixed(2), icon: "📅", color: "bg-white" },
                { label: "Free Leads Used", value: summary.freeLeadsUsed + "/3", icon: "🎁", color: "bg-teal-light" },
                { label: "Paid Leads", value: String(summary.paidLeads), icon: "💳", color: "bg-white" },
                { label: "Total Leads", value: String(summary.totalLeads), icon: "📊", color: "bg-blue-pale" },
              ].map(stat => (
                <div key={stat.label} className={stat.color + " rounded-2xl p-4 shadow-card"}>
                  <div className="text-2xl mb-2">{stat.icon}</div>
                  <div className="font-bold text-navy text-xl">{stat.value}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-4 shadow-card mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-navy text-sm">Free Lead Usage</div>
                <div className="text-xs text-gray-400">{summary.freeLeadsUsed} of 3 used</div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-3 mb-2">
                <div className="bg-teal rounded-full h-3 transition-all" style={{width: ((summary.freeLeadsUsed / 3) * 100) + "%"}}/>
              </div>
              <div className="text-xs text-gray-400">
                {summary.freeLeadsRemaining > 0 ? summary.freeLeadsRemaining + " free lead" + (summary.freeLeadsRemaining !== 1 ? "s" : "") + " remaining" : "Free trial complete — you are now on pay-per-lead"}
              </div>
            </div>

            <div className="bg-blue-pale rounded-2xl p-4">
              <div className="font-bold text-navy text-sm mb-3">How Lead Pricing Works</div>
              <div className="space-y-2">
                {[["🎁","First 3 leads are always free"],["💳","Pay only when you accept a lead"],["📊","Prices vary by service and job size"]].map(([icon, text]) => (
                  <div key={text} className="flex items-center gap-2">
                    <span className="text-lg">{icon}</span>
                    <span className="text-xs text-gray-600">{text}</span>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate("/lead-pricing")} className="text-mouna-blue font-bold text-xs mt-3 block">View full pricing table →</button>
            </div>
          </div>
        )}

        {!loading && activeTab === "history" && (
          <div className="animate-fade-in">
            {payments.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-blue-pale rounded-full flex items-center justify-center text-3xl mx-auto mb-4">💳</div>
                <div className="font-bold text-navy mb-2">No payments yet</div>
                <div className="text-sm text-gray-400">Your lead purchases will appear here</div>
              </div>
            ) : (
              <div className="space-y-3">
                {payments.map(payment => (
                  <div key={payment.id} className="bg-white rounded-2xl shadow-card overflow-hidden animate-fade-in">
                    <div className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-pale rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                          {CATEGORY_ICONS[payment.category] || "🏠"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-navy text-sm">{payment.category} — {payment.jobSize}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{formatDate(payment.date)}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-bold text-mouna-blue">${payment.amount.toFixed(2)}</div>
                          <span className="badge badge-green">Paid</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 flex justify-around py-2 px-2 z-30">
        {[["⊞","Dashboard","/home"],["💬","Messages","/messages"],["💰","Earnings","/pro/earnings"],["👤","Profile","/pro/edit"]].map(([icon, label, path]) => (
          <button key={label} onClick={() => navigate(path)} className="flex flex-col items-center gap-1 px-2 py-1">
            <span className="text-xl">{icon}</span>
            <span className={"text-xs font-semibold " + (window.location.pathname === path ? "text-mouna-blue" : "text-gray-400")}>{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
